import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ApproveButton from "@/components/ApproveButton";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function CartDrawer() {
  const { items, remove, updateQty, subtotal, open, setOpen } = useCart();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [business, setBusiness] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const handleCheckout = async () => {
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    if (!user && !email) { toast.error("Enter an email for guest checkout"); return; }
    setLoading(true);
    try {
      const r = await api.post("/checkout/session", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        origin_url: window.location.origin,
        email: user ? undefined : email,
        business_name: business || undefined,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full sm:max-w-md bg-[#FAF9F6] border-l border-[#E5E5E5] p-0 flex flex-col">
        <SheetHeader className="px-8 pt-8 pb-6 border-b border-[#E5E5E5]">
          <SheetTitle className="font-serif-editorial text-3xl text-[#1C1C1C]">Your Cart</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {items.length === 0 ? (
            <div data-testid="cart-empty" className="text-center py-24">
              <p className="overline mb-4">Empty</p>
              <p className="font-serif-editorial text-2xl">Curate your suite.</p>
              <Link to="/shop" onClick={() => setOpen(false)} className="mt-8 inline-block tt-btn-outline" data-testid="empty-cart-shop-btn">Browse Equipment</Link>
            </div>
          ) : (
            <div className="space-y-8">
              {items.map((item) => (
                <div key={item.slug} data-testid={`cart-item-${item.slug}`} className="flex gap-4 pb-6 border-b border-[#E5E5E5]">
                  <img src={item.image} alt={item.name} className="w-24 h-24 object-cover bg-[#F0EFEB]" />
                  <div className="flex-1">
                    <div className="overline mb-1">{item.category}</div>
                    <div className="font-serif-editorial text-lg leading-tight">{item.name}</div>
                    <div className="text-sm mt-2">{fmt(item.price)}</div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-[#E5E5E5]">
                        <button data-testid={`qty-decrease-${item.slug}`} onClick={() => updateQty(item.slug, item.quantity - 1)} className="p-2 hover:bg-[#F0EFEB] transition-colors duration-300"><Minus size={12} strokeWidth={1.5} /></button>
                        <span className="px-4 text-sm">{item.quantity}</span>
                        <button data-testid={`qty-increase-${item.slug}`} onClick={() => updateQty(item.slug, item.quantity + 1)} className="p-2 hover:bg-[#F0EFEB] transition-colors duration-300"><Plus size={12} strokeWidth={1.5} /></button>
                      </div>
                      <button data-testid={`remove-${item.slug}`} onClick={() => remove(item.slug)} className="text-[#5C5C5C] hover:text-[#1C1C1C] transition-colors duration-300"><X size={16} strokeWidth={1.25} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E5E5E5] px-8 py-6 space-y-5 bg-[#FAF9F6]">
            {!user && (
              <div className="space-y-3">
                <div className="overline">Guest Checkout</div>
                <input data-testid="guest-email-input" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className="tt-input" />
                <input data-testid="guest-business-input" type="text" placeholder="Med spa / business name (optional)" value={business} onChange={(e) => setBusiness(e.target.value)} className="tt-input" />
              </div>
            )}
            <div className="flex justify-between items-baseline">
              <span className="overline">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-serif-editorial text-3xl">{fmt(subtotal)}</span>
            </div>
            {items.length > 0 && (
              <div data-testid="cart-approve-bundle" className="pt-2">
                <div className="overline mb-2 text-[#5C5C5C]">{items.length > 1 ? "Finance the full recovery suite" : "Finance this order"}</div>
                <ApproveButton
                  theme="light"
                  onClick={() => setTimeout(() => setOpen(false), 0)}
                  items={items.map((i) => ({
                    model: i.name,
                    price: String(i.price),
                    qty: String(i.quantity),
                    type: i.category || "Equipment",
                  }))}
                />
              </div>
            )}
            <p className="text-xs text-[#5C5C5C]">Shipping & taxes calculated at checkout. White-glove installation included on all orders.</p>
            <button data-testid="checkout-btn" onClick={handleCheckout} disabled={loading} className="tt-btn-primary w-full">
              {loading ? "Preparing…" : "Proceed to Checkout"}
            </button>
            {!user && (
              <button onClick={() => { setOpen(false); nav("/login"); }} className="w-full text-xs text-center text-[#5C5C5C] hover:text-[#1C1C1C] transition-colors duration-300" data-testid="signin-from-cart">
                Have a trade account? Sign in
              </button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
